import React, { createContext, useContext, useEffect, useState } from 'react';
import { 
  onAuthStateChanged, 
  User as FirebaseUser,
  GoogleAuthProvider,
  signInWithPopup,
  signOut,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword
} from 'firebase/auth';
import { 
  doc, 
  getDoc, 
  setDoc, 
  serverTimestamp 
} from 'firebase/firestore';
import { auth, db } from '../lib/firebase';

interface UserProfile {
  uid: string;
  email: string | null;
  displayName: string | null;
  photoURL: string | null;
  role: 'user' | 'partner' | 'admin';
  registeredEvents: string[];
  savedArticles?: string[];
  readArticles?: string[];
  company?: string;
  industry?: string;
  mainInterest?: string;
  isComplete?: boolean;
}

interface AuthContextType {
  user: FirebaseUser | null;
  profile: UserProfile | null;
  loading: boolean;
  signInWithGoogle: () => Promise<void>;
  signInWithEmail: (email: string, pass: string) => Promise<void>;
  signUpWithEmail: (email: string, pass: string) => Promise<void>;
  updateProfile: (data: Partial<UserProfile>) => Promise<void>;
  logout: () => Promise<void>;
  isAdmin: boolean;
  isQuotaExceeded: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<FirebaseUser | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isQuotaExceeded, setIsQuotaExceeded] = useState(false);

  useEffect(() => {
    const handleGlobalQuotaExceeded = () => {
      setIsQuotaExceeded(true);
    };

    window.addEventListener('firebase-quota-exceeded', handleGlobalQuotaExceeded);
    return () => {
      window.removeEventListener('firebase-quota-exceeded', handleGlobalQuotaExceeded);
    };
  }, []);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setUser(user);
      if (user) {
        // Boostrapped admin check case-insensitive
        const emailLower = user.email ? user.email.toLowerCase() : '';
        const isBoostrappedAdmin = emailLower === 'sergioo.montiel@gmail.com' || emailLower === 'testadmin@treid.mx';
        
        try {
          // Sync user profile with Firestore
          const userRef = doc(db, 'users', user.uid);
          const userDoc = await getDoc(userRef);

          if (!userDoc.exists()) {
            const newProfile: UserProfile = {
              uid: user.uid,
              email: user.email,
              displayName: user.displayName,
              photoURL: user.photoURL,
              role: isBoostrappedAdmin ? 'admin' : 'user',
              registeredEvents: []
            };
            try {
              await setDoc(userRef, {
                ...newProfile,
                createdAt: serverTimestamp()
              });
            } catch (err) {
              console.warn("Could not save new profile (quota or network error):", err);
              if (err instanceof Error && (err.message.includes("quota") || err.message.includes("Quota") || err.message.includes("exhausted"))) {
                setIsQuotaExceeded(true);
              }
            }
            setProfile(newProfile);
            setIsAdmin(isBoostrappedAdmin);
          } else {
            const data = userDoc.data() as UserProfile;
            if (isBoostrappedAdmin && data.role !== 'admin') {
              data.role = 'admin';
              try {
                await setDoc(userRef, { role: 'admin' }, { merge: true });
                console.log("Successfully auto-promoted bootstrapped admin role in Firestore");
              } catch (promoErr) {
                console.warn("Skipping Firestore role auto-promote sync:", promoErr);
              }
            }
            setProfile(data);
            setIsAdmin(data.role === 'admin' || isBoostrappedAdmin);
          }
        } catch (dbError: any) {
          const errMsg = dbError instanceof Error ? dbError.message : String(dbError);
          const isQuota = /quota|Quota|exhausted|limit|billing/i.test(errMsg);
          if (isQuota) {
            console.warn("Firestore quota handled gracefully during auth profile sync:", dbError);
            setIsQuotaExceeded(true);
          } else {
            console.error("Firestore error during auth profile sync:", dbError);
          }
          if (dbError instanceof Error && (dbError.message.includes("quota") || dbError.message.includes("Quota") || dbError.message.includes("exhausted") || dbError.message.includes("permission-denied"))) {
            setIsQuotaExceeded(true);
          }
          // Build a graceful local fallback profile so the app doesn't break
          const fallbackProfile: UserProfile = {
            uid: user.uid,
            email: user.email,
            displayName: user.displayName || user.email?.split('@')[0] || 'Usuario TREID',
            photoURL: user.photoURL,
            role: isBoostrappedAdmin ? 'admin' : 'user',
            registeredEvents: []
          };
          setProfile(fallbackProfile);
          setIsAdmin(isBoostrappedAdmin);
        }
      } else {
        setProfile(null);
        setIsAdmin(false);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const signInWithGoogle = async () => {
    const provider = new GoogleAuthProvider();
    try {
      await signInWithPopup(auth, provider);
    } catch (error) {
      console.error("Error signing in with Google", error);
      throw error;
    }
  };

  const signInWithEmail = async (email: string, pass: string) => {
    try {
      await signInWithEmailAndPassword(auth, email, pass);
    } catch (error) {
      console.error("Error signing in with email", error);
      throw error;
    }
  };

  const signUpWithEmail = async (email: string, pass: string) => {
    try {
      await createUserWithEmailAndPassword(auth, email, pass);
    } catch (error) {
      console.error("Error signing up with email", error);
      throw error;
    }
  };

  const logout = async () => {
    try {
      await signOut(auth);
      localStorage.clear();
      sessionStorage.clear();
    } catch (error) {
      console.error("Error signing out", error);
    }
  };

  const updateProfile = async (data: Partial<UserProfile>) => {
    if (!user) return;
    const userRef = doc(db, 'users', user.uid);
    try {
      await setDoc(userRef, { 
        ...data, 
        uid: user.uid,
        email: user.email,
        isComplete: true 
      }, { merge: true });
      setProfile(prev => prev ? { ...prev, ...data, isComplete: true } : null);
      if (data.role) {
        const isBoostrappedAdmin = user.email ? ['sergioo.montiel@gmail.com', 'testadmin@treid.mx'].includes(user.email.toLowerCase()) : false;
        setIsAdmin(data.role === 'admin' || isBoostrappedAdmin);
      }
    } catch (error) {
      console.error("Error updating profile", error);
      if (error instanceof Error && (error.message.includes("quota") || error.message.includes("Quota") || error.message.includes("exhausted"))) {
        setIsQuotaExceeded(true);
      }
    }
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      profile, 
      loading, 
      signInWithGoogle, 
      signInWithEmail,
      signUpWithEmail,
      updateProfile, 
      logout,
      isAdmin,
      isQuotaExceeded
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
